import { CompiledView, SheetSource } from "./sheetTypes";

/**
 * Export View: the rows and columns on screen, in the order they are on screen,
 * and nothing else. viewToAoa below is the whole contract - short on purpose,
 * because it is the function a reviewer has to trust.
 *
 * Every value is written as TEXT. Employee number "007" and SSN "0448945654"
 * lose their leading zeros the moment a spreadsheet treats them as numbers, and
 * "1-2" becomes a date. The cost is that sums in the exported file are text too;
 * for payroll data that is the right way round, and the export menu says so.
 */

export type ExportFormat = "xlsx" | "csv";

export interface ExportRequest {
  source: SheetSource;
  view: CompiledView;
  format: ExportFormat;
  includeHeader: boolean;
  originalFileName: string;
  /** Sheet caption, or null for a single-sheet file. */
  sheetLabel: string | null;
}

export interface ExportResult {
  blob: Blob;
  fileName: string;
  rowCount: number;
  columnCount: number;
}

/** The exported matrix: header row (when the sheet has one) plus visible rows. */
export function viewToAoa(
  source: SheetSource,
  view: CompiledView,
  includeHeader: boolean,
): string[][] {
  const out: string[][] = [];
  const columns = view.columns;

  if (includeHeader) {
    const header: string[] = new Array(columns.length);
    for (let c = 0; c < columns.length; c++) {
      header[c] = columns[c].label || columns[c].letter;
    }
    out.push(header);
  }

  for (let r = 0; r < view.rows.length; r++) {
    const cells = view.rows[r].cells;
    const row: string[] = new Array(columns.length);
    for (let c = 0; c < columns.length; c++) {
      const value = cells[columns[c].sourceIndex];
      row[c] = value == null ? "" : value;
    }
    out.push(row);
  }

  return out;
}

export function estimateExportCells(
  view: CompiledView,
  includeHeader: boolean,
): number {
  return (view.rows.length + (includeHeader ? 1 : 0)) * view.columns.length;
}

/** Excel rejects : \ / ? * [ ] in sheet names and truncates at 31 characters. */
export function sanitizeSheetName(name: string): string {
  const cleaned = (name || "View").replace(/[:\\/?*[\]]/g, " ").trim();
  const trimmed = cleaned.slice(0, 31);
  return trimmed || "View";
}

const UNSAFE_FILENAME = /[\\/:*?"<>|]/g;

export function exportViewFileName(
  originalFileName: string,
  sheetLabel: string | null,
  format: ExportFormat,
): string {
  const dot = originalFileName.lastIndexOf(".");
  const base =
    dot > 0 ? originalFileName.slice(0, dot) : originalFileName || "export";
  const sheetPart = sheetLabel ? ` - ${sheetLabel}` : "";
  const name = `${base}${sheetPart} - view`
    .replace(UNSAFE_FILENAME, " ")
    .replace(/\s+/g, " ")
    .trim();
  return `${name.slice(0, 120)}.${format}`;
}

/** Tab separated, which is what a spreadsheet expects from the clipboard. */
export function matrixToTsv(matrix: string[][]): string {
  const lines: string[] = new Array(matrix.length);
  for (let r = 0; r < matrix.length; r++) {
    const row = matrix[r];
    const cells: string[] = new Array(row.length);
    for (let c = 0; c < row.length; c++) {
      // Tabs and newlines inside a value would break the grid a paste rebuilds.
      cells[c] = (row[c] ?? "").replace(/[\t\r\n]+/g, " ");
    }
    lines[r] = cells.join("\t");
  }
  return lines.join("\r\n");
}

export const CSV_BOM = "\uFEFF";

/**
 * CSV text with the byte order mark: without it Excel reads UTF-8 as its local
 * code page and mangles every non-ASCII name in the file.
 */
export async function buildCsvText(matrix: string[][]): Promise<string> {
  const Papa = (await import("papaparse")).default;
  return CSV_BOM + Papa.unparse(matrix, { newline: "\r\n", quotes: true });
}

export async function buildXlsxBytes(
  matrix: string[][],
  sheetName: string,
): Promise<ArrayBuffer> {
  const XLSX = await import("xlsx");
  // Explicit string cells: aoa_to_sheet passes a cell object through untouched,
  // so nothing can re-infer "007" as a number later.
  const cells = matrix.map((row) => row.map((value) => ({ t: "s", v: value })));
  const worksheet = XLSX.utils.aoa_to_sheet(cells as unknown as unknown[][]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    sanitizeSheetName(sheetName),
  );
  return XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
    compression: true,
  }) as ArrayBuffer;
}

/** One sheet of a multi-sheet export: its source, its view and its tab name. */
export interface SheetExportPart {
  source: SheetSource;
  view: CompiledView;
  label: string;
  includeHeader: boolean;
}

/**
 * Every sheet of the workbook in one file, each with its own view applied. A
 * sheet nobody opened has no view of its own and goes out whole - the export
 * menu says so, because "all sheets" must not quietly mean "all sheets, some of
 * them filtered by something you set an hour ago and forgot".
 */
export async function exportAllSheets(
  parts: SheetExportPart[],
  originalFileName: string,
): Promise<ExportResult> {
  const XLSX = await import("xlsx");
  const workbook = XLSX.utils.book_new();
  let rowCount = 0;
  let columnCount = 0;
  const usedNames: Record<string, true> = {};

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    const matrix = viewToAoa(part.source, part.view, part.includeHeader);
    const cells = matrix.map((row) =>
      row.map((value) => ({ t: "s", v: value })),
    );
    const worksheet = XLSX.utils.aoa_to_sheet(cells as unknown as unknown[][]);

    // Excel rejects duplicate sheet names, and sanitising can create them.
    let name = sanitizeSheetName(part.label || `Sheet ${i + 1}`);
    let suffix = 2;
    while (usedNames[name.toLowerCase()]) {
      name = sanitizeSheetName(`${name.slice(0, 28)} ${suffix}`);
      suffix++;
    }
    usedNames[name.toLowerCase()] = true;

    XLSX.utils.book_append_sheet(workbook, worksheet, name);
    rowCount += part.view.rows.length;
    if (part.view.columns.length > columnCount) {
      columnCount = part.view.columns.length;
    }
  }

  const out = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
    compression: true,
  }) as ArrayBuffer;

  return {
    blob: new Blob([out], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    fileName: exportViewFileName(originalFileName, null, "xlsx"),
    rowCount,
    columnCount,
  };
}

export async function exportView(req: ExportRequest): Promise<ExportResult> {
  const matrix = viewToAoa(req.source, req.view, req.includeHeader);
  const blob =
    req.format === "csv"
      ? new Blob([await buildCsvText(matrix)], {
          type: "text/csv;charset=utf-8",
        })
      : new Blob(
          [
            await buildXlsxBytes(
              matrix,
              req.sheetLabel || req.source.name || "View",
            ),
          ],
          {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
        );

  return {
    blob,
    fileName: exportViewFileName(
      req.originalFileName,
      req.sheetLabel,
      req.format,
    ),
    rowCount: req.view.rows.length,
    columnCount: req.view.columns.length,
  };
}
